import { Fragment, useEffect, useRef, useState } from "react";
import { type User } from "../types";
import SimpeRenderServerSideContent from "../../common/components/SimpeRenderServerSideContent";
import PageHeaderElement from "../components/PageHeaderElement";
import DesktopPersonalNav from "../components/headerMenu/DesktopPersonalNav";
import { userIsCustomer, userIsLoggedIn, userIsRegistered } from "../../utils/roles";
import { LanguageSelector } from "../components/LanguageSelector/LanguageSelector";
import { MediasLogo } from "../components/TopBar/MediasLogo";
import SearchBox from "../components/headerMenu/Autosuggest/SearchBox";
import { CompanyLogo } from "../../common/components/companyLogo/CompanyLogo";
import { getMediasVariant } from "../components/TopBar/HeaderCircle";

import { UserNavigation } from "../components/TopBar/UserNavigation";
import { HeaderLoginElement } from "../components/TopBar/HeaderLoginElement";
import { DesktopCategoryNavigation } from "~/feature/CategoryNavigation/v1/DesktopCategoryNavigation";
import { MobileCategoryNavigation } from "~/feature/CategoryNavigation/v1/MobileCategoryNavigation";
import { TabletCategoryNavigation } from "~/feature/CategoryNavigation/v1/TabletCategoryNavigation";
import { Divider } from "../../common/components/Divider";
import { MobilePersonalNav } from "../components/headerMenu/MobilePersonalNav";
import { useAffiliateTracking } from "~/feature/Affiliate/useAffiliateTracking";
import { useOnMount } from "../../common/hooks/useOnMount";
import { useDispatch } from "react-redux";
import { populateInitialComparisonList } from "../../ProductComparison/redux/actions";
import { ScrollToTop } from "~/Header/components/ScrollToTop/ScrollToTop";
import { SessionExpirePopupContainer } from "~/SessionExpire/useSessionExpirePopup";
import { isDebugLanguageEnabled } from "~/utils/language/isDebugLanguageEnabled";
import { DebugLanguageToggle } from "~/Header/components/DebugLanguageToggle";
import { Link, LinkType } from "~/common/components/Link";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { SCAN_DMC_PAGE } from "~/utils/url/staticUrls";
import { TopBannersSection } from "~/Header/components/TopBannersSection/TopBannersSection";
import GlobalMessage from "~/Header/components/GlobalMessage";
import { MergeCustomerCart } from "~/feature/MergeCustomerCart/useMergeCustomerCart";
import classNames from "classnames";
import { isCheckoutPage, isDatasheetPage, isForkPage } from "~/utils/url/urlUtils";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { IconButton } from "@mui/material";
import classes from "./HeaderNavigationComponent.module.less";
import { useLogoutMessageHandler } from "~/common/hooks/useLogoutMessageHandler";
import RedirectToPDPComponent from "~/Header/jspComponents/Redirect/RedirectToPDPComponent";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { useTranslation } from "react-i18next";
import { FCCBanner, isFCCBannerVisible } from "~/FreeCapacityPush/components/FCCBanner/FCCBanner";
import { ProductFruits } from "react-product-fruits";
import { Snackbar, SnackbarCloseButton } from "~/common/m3/Snackbar/Snackbar";
import { isNewNavigationEnabled } from "~/utils/features";
import { CategoryNavigation } from "~/feature/CategoryNavigation/v2/CategoryNavigation";
import CheckoutHeader from "~/common/components/CheckoutHeader/CheckoutHeader";

interface Props {
  user?: User;
  isSearchPage: boolean;
  showEdit: boolean;
}

/**
 *   The entire navigation on top of the page. Consists of three parts:
 *   * Top bar with search bar, company logo etc.
 *   * Middle navigation in HeaderMenu
 */
export const HeaderNavigation = ({ user, showEdit, isSearchPage }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // Anonymous - logged out. Go public - logged out when go public is enabled.
  const loggedIn = userIsLoggedIn();

  const mediasVariant = getMediasVariant();

  const screenSize = useNumericScreenSize();

  const isMinPhone = screenSize >= useNumericScreenSize.phone;
  const isMinTablet = screenSize >= useNumericScreenSize.tablet;
  const isMinDesktop = screenSize >= useNumericScreenSize.desktopS;

  const menuAnchorRef = useRef<HTMLDivElement | null>(null);

  const { val: topBar, setTrue: displayTopBar, setFalse: hideTopBar } = useBooleanState(true);

  const getCodesList = () => {
    if (window.SCHA.productComparisonList.codesList) {
      return window.SCHA.productComparisonList.codesList.map(String);
    }
    return [];
  };
  const codesList = getCodesList();

  const showLoginSnackbar = window.sessionStorage.getItem("accountChanged") === "true";
  const showDeleteAccountSuccessSnackbar = window.sessionStorage.getItem("accountDeleted") === "true";

  const { val: overlay, setTrue: showOverlay, setFalse: hideOverlay } = useBooleanState(false);
  const { val: recentSearches, setTrue: showRecentSearches, setFalse: hideRecentSearches } = useBooleanState(false);
  const { val: suggestions, setFalse: hideSuggestions, setTrue: showSuggestions } = useBooleanState(false);
  const {
    val: loginSnackbar,
    setFalse: hideLoginSnackbar,
    setTrue: openLoginSnackbar,
  } = useBooleanState(showLoginSnackbar);

  const { val: deleteAccountSuccessSnackbar, setFalse: hideDeleteAccountSuccessSnackbar } = useBooleanState(
    showDeleteAccountSuccessSnackbar,
  );

  useOnMount(() => {
    dispatch(populateInitialComparisonList(codesList));
  });

  useEffect(() => {
    displayTopBar();
  }, [isMinDesktop]);

  // Tracking in here, because this element is always created
  useAffiliateTracking();
  useLogoutMessageHandler();
  const showDebugLanguage = isDebugLanguageEnabled();

  const applyStickyNav = () => {
    // TODO - Fix this...
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [windowPosition, setWindowPosition] = useState(0);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      const listenScrollEvent = () => {
        setWindowPosition(window.scrollY);
      };
      window.addEventListener("scroll", listenScrollEvent);
      return () => window.removeEventListener("scroll", listenScrollEvent);
    }, []);

    if (windowPosition > 0) {
      return "stickyTop";
    }
  };

  const shouldAddProductFruits = window.SCHA.config.enableProductFruitsIntegration && userIsCustomer();
  const categoryNavigation = <CategoryNavigation navigationRef={menuAnchorRef} />;

  return (
    <>
      <GlobalMessage />
      <RedirectToPDPComponent />
      <SessionExpirePopupContainer />
      <MergeCustomerCart />
      <Snackbar
        className={classes.loginSnackbar}
        message={t("accountSelection.accountSelectionSuccess")}
        open={loginSnackbar}
        onClose={() => {
          window.sessionStorage.setItem("accountChanged", "false");
          hideLoginSnackbar();
        }}
      />

      <Snackbar
        message={t("feature.deleteAccount.success")}
        open={deleteAccountSuccessSnackbar}
        onClose={() => {
          window.sessionStorage.setItem("accountDeleted", "false");
          hideDeleteAccountSuccessSnackbar();
        }}
        action={
          <SnackbarCloseButton
            onClick={() => {
              window.sessionStorage.setItem("accountDeleted", "false");
              hideDeleteAccountSuccessSnackbar();
            }}
          />
        }
      />

      {shouldAddProductFruits && (
        <ProductFruits
          workspaceCode="lfRfdsZ3tuukhX8r"
          language={(window.SCHA.config.language ?? "en").substring(0, 2)}
          user={{
            username: window.SCHA.user.uid,
            role: getUserRole(),
          }}
        />
      )}

      <div className={classNames(window.stickyNavigation && applyStickyNav())}>
        {overlay && <div className={classes.overlayContainer} />}
        {isFCCBannerVisible() && <FCCBanner />}
        <TopBannersSection />

        {topBar && (
          <PageHeaderElement className={"scha__logo-header"}>
            {!showDebugLanguage && <MediasLogo variant={mediasVariant} href={true} />}
            {showDebugLanguage && <DebugLanguageToggle />}

            {isMinTablet && (
              <>
                <Divider className={"scha__header__divider"} orientation={"vertical"} flexItem />
                {loggedIn && user && <UserNavigation user={user} showEdit={showEdit} />}
                {!loggedIn && <HeaderLoginElement />}

                <LanguageSelector />

                {isMinTablet && isMinDesktop && (
                  <div className={classes.input}>
                    <SearchBox
                      recentSearches={recentSearches}
                      showRecentSearches={showRecentSearches}
                      hideRecentSearches={hideRecentSearches}
                      suggestions={suggestions}
                      showSuggestions={showSuggestions}
                      hideSuggestions={hideSuggestions}
                      isSearchPage={isSearchPage}
                      overlay={overlay}
                      showOverlay={showOverlay}
                      hideOverlay={hideOverlay}
                    />
                  </div>
                )}
              </>
            )}
            {/* removed becuase of CR SCHAEFF-36763 */}
            {/* {screenSize >= useNumericScreenSize.desktopS && <DesktopScanDMCTeaser />} */}

            <CompanyLogo className="scha__header__logo" />
          </PageHeaderElement>
        )}

        <PageHeaderElement ref={menuAnchorRef} className={"scha__header-menu"}>
          {!isMinDesktop && (
            <>
              <div className="scha__mobile-navigation__left">
                {!topBar && (
                  <IconButton color="primary" onClick={displayTopBar}>
                    <Icon type={IconType.ChevronLeft} />
                  </IconButton>
                )}
                {!isNewNavigationEnabled() && isMinPhone && !isMinTablet && topBar && (
                  <MobileCategoryNavigation menuAnchorRef={menuAnchorRef} />
                )}
                {isNewNavigationEnabled() && isMinPhone && !isMinDesktop && topBar && categoryNavigation}
                {!isNewNavigationEnabled() && isMinTablet && isMinPhone && topBar && (
                  <TabletCategoryNavigation menuAnchorRef={menuAnchorRef} />
                )}
                <div className={classes.input}>
                  <SearchBox
                    recentSearches={recentSearches}
                    showRecentSearches={showRecentSearches}
                    hideRecentSearches={hideRecentSearches}
                    suggestions={suggestions}
                    showSuggestions={showSuggestions}
                    hideSuggestions={hideSuggestions}
                    topBar={topBar}
                    displayTopBar={displayTopBar}
                    hideTopBar={hideTopBar}
                    showOverlay={showOverlay}
                    hideOverlay={hideOverlay}
                    isSearchPage={isSearchPage}
                    overlay={overlay}
                  />
                </div>
              </div>

              {topBar && (
                <div className={"scha__mobile-navigation__right"}>
                  {isMinTablet && isMinPhone && <MobilePersonalNav />}
                  {isMinPhone && !isMinTablet && (
                    <Fragment>
                      {
                        <Link
                          className={classNames(
                            "scha__mobile-navigation__button scha__mobile-navigation__button--dmc",
                            userIsLoggedIn() && "scha__mobile-navigation__button--dmc--logged-in",
                          )}
                          variant={LinkType.NAVIGATION}
                          href={SCAN_DMC_PAGE}
                          ocs-tracking-aux-marker="access-icon"
                        >
                          <Icon type={IconType.DmcScan} size={32} />
                        </Link>
                      }
                      {loggedIn && user && <UserNavigation user={user} showEdit={showEdit} />}
                      {!loggedIn && user && <HeaderLoginElement />}
                    </Fragment>
                  )}
                </div>
              )}
            </>
          )}

          {isMinDesktop && (
            <>
              <div className="scha__header-menu__navigation">
                {!isNewNavigationEnabled() && <DesktopCategoryNavigation />}
                {isNewNavigationEnabled() && categoryNavigation}
              </div>

              <div className="scha__header-menu__personal-nav">
                <DesktopPersonalNav />
              </div>
            </>
          )}
        </PageHeaderElement>

        {/* Tour is completely removed right now - see SCHAEFF-14767 */}

        <ScrollToTop />
      </div>
    </>
  );
};

const StaticPageHeader = () => {
  return (
    <div>
      <PageHeaderElement className={"scha__logo-header"}>
        <MediasLogo variant={"general"} href={true} />
        <CompanyLogo className="scha__header__logo" />
      </PageHeaderElement>

      <SimpeRenderServerSideContent selector={"#BreadcrumbSlot"} />
    </div>
  );
};

export function createHeaderNavigation(props: Props) {
  if (isForkPage() || isDatasheetPage()) {
    return <StaticPageHeader />;
  }
  if (isCheckoutPage()) {
    return <CheckoutHeader />;
  }

  return <HeaderNavigation {...props} />;
}

function getUserRole() {
  if (userIsCustomer()) {
    return "Business";
  }
  if (userIsRegistered()) {
    return "Plus";
  }
  return "Anonymous";
}
